import { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { clsx } from 'clsx';
import { Bell, Eye, X } from 'lucide-react';
import { useAuth } from '../../lib/auth/AuthContext';
import { useNotifications } from '../../lib/data/domains';
import { Avatar } from '../ui/Avatar';
import { IconAction } from '../ui/IconAction';
import { ThemeSwitch } from './ThemeSwitch';
import { ImpersonateSheet } from '../../features/admin/ImpersonateSheet';

export function TopBar() {
  const { ctx, user, isAdmin, isImpersonating, impersonatedUser, stopImpersonation } = useAuth();
  const [sheetOpen, setSheetOpen] = useState(false);
  const unread = useNotifications(ctx?.user.id ?? null).data.filter((n) => !n.read).length;
  const bell = ctx ? `/app/${ctx.user.role}/notifikasi` : '/';
  const profile = ctx ? `/app/${ctx.user.role}/profil` : '/';

  return (
    <header className="glass-bar safe-top sticky top-0 z-40 md:h-auto">
      <div className="mx-auto flex h-14 max-w-[1080px] items-center justify-between px-4 md:h-12 md:px-8">
        {/* Mobile brand — desktop relies on the sidebar */}
        <Link
          to={ctx ? `/app/${ctx.user.role}` : '/'}
          className="flex items-center gap-2 md:hidden"
        >
          <span className="grid size-7 place-items-center rounded-none bg-accent text-[15px] font-black text-white">
            B
          </span>
          <span className="text-[17px] font-bold tracking-tight text-ink">Bimbingan</span>
        </Link>

        {/* Desktop: page-title slot filled by the current screen */}
        <div className="hidden text-[13px] font-semibold text-faint md:block" />

        <div className="flex items-center gap-1 md:gap-0.5">
          <ThemeSwitch />
          {isAdmin && !isImpersonating && (
            <IconAction
              label="Pratinjau peran"
              tone="soft"
              size="sm"
              onClick={() => setSheetOpen(true)}
            >
              <Eye className="size-4" />
            </IconAction>
          )}
          <NavLink
            to={bell}
            className="relative grid size-9 place-items-center rounded-none text-muted transition-colors hover:bg-surface-2 press"
            aria-label="Notifikasi"
          >
            <Bell className="size-[19px]" />
            {unread > 0 && (
              <span className="absolute right-0.5 top-0.5 grid min-w-[18px] place-items-center rounded-none bg-danger px-1 text-[10px] font-bold text-white">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </NavLink>
          <NavLink to={profile} className={clsx('ml-1 rounded-none press')} aria-label="Profil">
            <Avatar name={user?.name} size="sm" />
          </NavLink>
        </div>
      </div>

      {isImpersonating && impersonatedUser && (
        <div className="mx-auto flex max-w-[1080px] items-center gap-2 px-4 pb-2 md:px-8">
          <div className="flex min-w-0 flex-1 items-center gap-2.5 rounded-none bg-accent-soft px-3 py-1.5">
            <Avatar name={impersonatedUser.name} size="xs" />
            <div className="min-w-0 flex-1">
              <div className="truncate text-[12px] font-bold text-accent-strong">
                Menampilkan sebagai {impersonatedUser.name}
              </div>
            </div>
          </div>
          <button
            onClick={() => setSheetOpen(true)}
            className="shrink-0 rounded-none bg-surface-2 px-3 py-1.5 text-[12px] font-bold text-ink press"
          >
            Ganti
          </button>
          <button
            onClick={stopImpersonation}
            aria-label="Kembali ke akun admin"
            className="grid size-7 shrink-0 place-items-center rounded-none bg-surface-2 text-muted press"
          >
            <X className="size-3.5" />
          </button>
        </div>
      )}

      <ImpersonateSheet open={sheetOpen} onClose={() => setSheetOpen(false)} />
    </header>
  );
}
